import { revalidateLogic } from "@tanstack/react-form";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, redirect, useRouter } from "@tanstack/react-router";
import { type SubmitEvent, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import chomeIcon from "@/assets/icons/googlechrome.svg";
import logo from "@/assets/landing/logo-rocortado.png";
import { useAppForm } from "@/components/form/tanstack-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/input-password";
import { parsePocketBaseError } from "@/lib/errors/pocketbase";
import { pb } from "@/lib/pocketbase";
import { Collections } from "@/types/pocketbase.ts";

const parseRedirectTo = (redirectTo?: string) => {
	if (redirectTo === "/") {
		return "/dashboard";
	}

	return redirectTo;
};

const validateSearch = z.object({
	redirectTo: z.string().optional(),
	invitation_token: z.string().optional(),
});

export const Route = createFileRoute("/auth/login")({
	validateSearch,
	beforeLoad: ({ search }) => {
		if (pb.authStore.isValid) {
			throw redirect({ to: "/dashboard", search });
		}
	},
	component: RouteComponent,
});

const PASSWORD_LENGTH = 6;

const formSchema = z.object({
	identity: z.email(),
	password: z.string().min(PASSWORD_LENGTH),
});

type FormValues = z.infer<typeof formSchema>;

function RouteComponent() {
	const router = useRouter();
	const search = Route.useSearch();
	const { t } = useTranslation();
	const redirectTo = parseRedirectTo(search?.redirectTo);

	const login = useMutation({
		mutationFn: (data: FormValues) => {
			toast.loading(
				t(($) => $.modules.auth.login.status.loading.title),
				{
					id: "login",
					description: t(($) => $.modules.auth.login.status.loading.message),
				}
			);

			return pb.collection(Collections.Doctors).authWithPassword(data.identity.toLowerCase(), data.password);
		},
		onSuccess: async () => {
			toast.success(
				t(($) => $.modules.auth.login.status.success.title),
				{
					id: "login",
					description: t(($) => $.modules.auth.login.status.success.message),
				}
			);

			if (search.invitation_token) {
				await router.navigate({ to: "/dashboard", search });
				return;
			}

			await router.navigate({ to: redirectTo });
		},
		onError: (error) => {
			const parsed = parsePocketBaseError(error, defaultValues);

			if (parsed.unknown) {
				return toast.error(
					t(($) => $.modules.auth.login.status.error.title),
					{
						id: "login",
						description: parsed.message || t(($) => $.modules.auth.login.status.error.message),
					}
				);
			}

			form.setErrorMap({ onDynamic: parsed.errorMap });
			toast.dismiss("login");
		},
	});

	const defaultValues: FormValues = {
		identity: "",
		password: "",
	};

	const form = useAppForm({
		defaultValues,
		validationLogic: revalidateLogic({
			mode: "submit",
			modeAfterSubmission: "change",
		}),
		validators: { onDynamic: formSchema },
		onSubmit: ({ value }) => {
			login.mutate(value);
		},
	});

	const handleSubmit = useCallback(
		(e: SubmitEvent) => {
			e.preventDefault();
			e.stopPropagation();
			form.handleSubmit();
		},
		[form]
	);

	const handleGoogleLogin = async () => {
		try {
			await pb.collection(Collections.Doctors).authWithOAuth2({
				provider: "google",
			});

			toast.success(
				t(($) => $.modules.auth.login.status.success.title),
				{
					id: "login",
					description: t(($) => $.modules.auth.login.status.success.message),
				}
			);

			if (search.invitation_token) {
				await router.navigate({ to: "/dashboard", search });
				return;
			}

			await router.navigate({ to: redirectTo });
		} catch (err) {
			const error = err as Error;

			let description: string = t(($) => $.modules.auth.login.status.error.message);
			if (error.message) {
				description = error.message;
			}

			toast.error(
				t(($) => $.modules.auth.login.status.error.title),
				{
					id: "login",
					description,
				}
			);
		}
	};

	return (
		<div className="mx-auto w-full max-w-md space-y-8">
			<div className="flex flex-col items-center">
				<img alt="Logo" height={"192.667"} src={logo} width={"447.967"} />
				<h2 className="mt-6 text-center font-bold text-3xl text-foreground tracking-tight">
					{t(($) => $.modules.auth.login.title)}
				</h2>
			</div>

			<form.AppForm>
				<form className="space-y-6" onSubmit={handleSubmit}>
					<form.AppField name="identity">
						{(field) => (
							<field.Field>
								<field.FieldLabel>{t(($) => $.modules.auth.common.fields.email.label)}</field.FieldLabel>
								<field.FieldControl>
									<Input
										autoComplete="email"
										onBlur={field.handleBlur}
										onChange={(e) => field.handleChange(e.target.value)}
										placeholder={t(($) => $.modules.auth.common.fields.email.placeholder)}
										type="email"
										value={field.state.value}
									/>
								</field.FieldControl>

								<field.FieldError />
							</field.Field>
						)}
					</form.AppField>

					<form.AppField name="password">
						{(field) => (
							<field.Field>
								<field.FieldLabel>{t(($) => $.modules.auth.common.fields.password.label)}</field.FieldLabel>
								<field.FieldControl>
									<PasswordInput
										autoComplete="current-password"
										onBlur={field.handleBlur}
										onChange={(e) => field.handleChange(e.target.value)}
										placeholder={t(($) => $.modules.auth.common.fields.password.placeholder)}
										value={field.state.value}
									/>
								</field.FieldControl>
								<field.FieldDescription>
									<Link className="text-primary hover:underline" to="/auth/forgot-password">
										{t(($) => $.modules.auth.login.buttons.forgotPassword)}
									</Link>
								</field.FieldDescription>
								<field.FieldError />
							</field.Field>
						)}
					</form.AppField>

					<Button className="w-full" disabled={login.isPending} type="submit">
						{t(($) => $.modules.auth.login.buttons.submit)}
					</Button>
				</form>
			</form.AppForm>

			<Button className="inline-flex w-full gap-2" onClick={handleGoogleLogin} variant="outline">
				<img alt="Google chrome" height={20} src={chomeIcon} width={20} />
				{t(($) => $.modules.auth.login.buttons.google)}
			</Button>

			<div className="mt-4 text-center">
				<span className="text-muted-foreground text-sm">
					{t(($) => $.modules.auth.login.buttons.register.question)}
				</span>{" "}
				<Link className="font-medium text-primary text-sm hover:underline" to="/auth/register">
					{t(($) => $.modules.auth.login.buttons.register.answer)}
				</Link>
			</div>
		</div>
	);
}
